/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: LdapConfigurationForm.tsx
 🗓️ Created: 2026-01-25
 🧩 Description:
    LDAP / Active Directory configuration form for directory
    integration (user sync, authentication).
    Uses standardized 4-column layout matching Extensions form.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { ModuleFormHeader } from '../../../components/layouts';
import { FormBox } from '../../../components/ui/FormBox';
import { Server, Lock, RefreshCw, TestTube2 } from 'lucide-react';
import { FixedActionBar } from '../../../components/ui/FixedActionBar';
import { Toggle } from '../../../components/ui/Toggle';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { api } from '../../../lib/api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useSaveWithAction } from '../../../hooks/useSaveWithAction';

interface LdapConfig {
    name: string;
    enabled: boolean;
    // Server settings
    serverUrl: string;
    port: number;
    useSsl: boolean;
    useTls: boolean;
    // Bind credentials
    bindDn: string;
    bindPassword: string;
    // Search settings
    baseDn: string;
    userFilter: string;
    groupFilter: string;
    // Attribute mappings
    usernameAttribute: string;
    emailAttribute: string;
    displayNameAttribute: string;
    phoneAttribute: string;
    // Sync settings
    syncEnabled: boolean;
    syncInterval: number;
}

const defaultConfig: LdapConfig = {
    name: '',
    enabled: true,
    serverUrl: '',
    port: 389,
    useSsl: false,
    useTls: true,
    bindDn: '',
    bindPassword: '',
    baseDn: '',
    userFilter: '(objectClass=person)',
    groupFilter: '(objectClass=group)',
    usernameAttribute: 'sAMAccountName',
    emailAttribute: 'mail',
    displayNameAttribute: 'displayName',
    phoneAttribute: 'telephoneNumber',
    syncEnabled: false,
    syncInterval: 60,
};

export const LdapConfigurationForm = () => {
    const { provider, connectionId } = useParams<{ provider: string; connectionId: string }>();
    const isActiveDirectory = provider === 'active_directory';
    const isNewConnection = connectionId === 'new';
    const editConnectionId = connectionId && connectionId !== 'new' ? connectionId : null;
    const isEditMode = !!editConnectionId;

    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const [config, setConfig] = useState<LdapConfig>({
        ...defaultConfig,
        port: isActiveDirectory ? 636 : 389,
        useSsl: isActiveDirectory,
        useTls: !isActiveDirectory,
    });
    const [originalConfig, setOriginalConfig] = useState<LdapConfig>(config);
    const [isSaving, setIsSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connected' | 'error'>('disconnected');

    // Fetch existing connection
    const fetchConnection = useCallback(async () => {
        if (!editConnectionId) return;
        try {
            const response = await api.get(`/api/integrations/crm/connections/${editConnectionId}`);
            const data = response.data;
            if (data) {
                const settings = data.settings || {};
                const credentials = data.credentials || {};
                const newConfig: LdapConfig = {
                    name: data.name || '',
                    enabled: data.status === 'CONNECTED',
                    serverUrl: settings.serverUrl || '',
                    port: settings.port || (isActiveDirectory ? 636 : 389),
                    useSsl: settings.useSsl ?? isActiveDirectory,
                    useTls: settings.useTls ?? !isActiveDirectory,
                    bindDn: credentials.bindDn || '',
                    bindPassword: '',
                    baseDn: settings.baseDn || '',
                    userFilter: settings.userFilter || '(objectClass=person)',
                    groupFilter: settings.groupFilter || '(objectClass=group)',
                    usernameAttribute: settings.usernameAttribute || 'sAMAccountName',
                    emailAttribute: settings.emailAttribute || 'mail',
                    displayNameAttribute: settings.displayNameAttribute || 'displayName',
                    phoneAttribute: settings.phoneAttribute || 'telephoneNumber',
                    syncEnabled: settings.syncEnabled ?? false,
                    syncInterval: settings.syncInterval || 60,
                };
                setConfig(newConfig);
                setOriginalConfig(JSON.parse(JSON.stringify(newConfig)));
                setConnectionStatus(data.status === 'CONNECTED' ? 'connected' : 'disconnected');
            }
        } catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to load connection');
        }
    }, [editConnectionId, isActiveDirectory, showError]);

    useEffect(() => {
        fetchConnection();
    }, [fetchConnection]);

    // Check for unsaved changes
    const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);

    // Handle save
    const handleSave = async () => {
        try {
            setIsSaving(true);
            const providerUpper = isActiveDirectory ? 'ACTIVE_DIRECTORY' : 'LDAP';

            const payload = {
                name: config.name,
                provider: providerUpper,
                authType: 'API_KEY',
                settings: {
                    serverUrl: config.serverUrl,
                    port: config.port,
                    useSsl: config.useSsl,
                    useTls: config.useTls,
                    baseDn: config.baseDn,
                    userFilter: config.userFilter,
                    groupFilter: config.groupFilter,
                    usernameAttribute: config.usernameAttribute,
                    emailAttribute: config.emailAttribute,
                    displayNameAttribute: config.displayNameAttribute,
                    phoneAttribute: config.phoneAttribute,
                    syncEnabled: config.syncEnabled,
                    syncInterval: config.syncInterval,
                },
                credentials: {
                    bindDn: config.bindDn,
                    ...(config.bindPassword && { bindPassword: config.bindPassword }),
                },
            };

            let savedData;
            if (editConnectionId) {
                const response = await api.patch(`/api/integrations/crm/connections/${editConnectionId}`, payload);
                savedData = response.data;
                showSuccess(t('integrations.messages.updateSuccess'));
            } else {
                const response = await api.post('/api/integrations/crm/connect-token', payload);
                savedData = response.data;
                showSuccess(t('integrations.messages.createSuccess'));
            }
            setOriginalConfig(JSON.parse(JSON.stringify(config)));
            return savedData || { id: editConnectionId };
        } catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to save');
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        setConfig(JSON.parse(JSON.stringify(originalConfig)));
    };

    // Test connection
    const handleTestConnection = async () => {
        setTesting(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            setConnectionStatus('connected');
            showSuccess('Connection successful!');
        } catch (err) {
            setConnectionStatus('error');
            showError(err instanceof Error ? err.message : 'Connection failed');
        } finally {
            setTesting(false);
        }
    };

    // Save with action handler (for Save and Close, Save and New, etc.)
    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSave,
        listPath: '/settings/integrations',
        newPath: `/settings/integrations/${provider}/new`,
        mode: isEditMode ? 'edit' : 'create',
        getEditPath: (savedData) => `/settings/integrations/${provider}/${savedData?.id || editConnectionId}`,
    });

    const updateConfig = <K extends keyof LdapConfig>(field: K, value: LdapConfig[K]) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    const providerName = isActiveDirectory ? 'Active Directory' : 'LDAP';
    const providerIcon = <Server className="h-5 w-5 text-primary" />;

    return (
        <div className="p-6 pb-32">
            <ModuleFormHeader
                icon={providerIcon}
                title={isNewConnection ? `${t('integrations.connect')} ${providerName}` : `${t('common.edit')} ${providerName}`}
                backButtonText={t('common.back')}
                onBack={() => navigate('/settings/integrations')}
                actions={[]}
            />

            <div className="space-y-4 mt-8">
                {/* Server Settings */}
                <FormBox
                    title={t('integrations.ldap.serverSettings')}
                    icon={<Server className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
                        {/* Row 1: Connection Name | Input | Server URL | Input */}
                        <SimpleTooltip content="Friendly name for this connection" title={t('common.name')} position="top" maxWidth="300px">
                            <Label htmlFor="name" required className="text-left">{t('common.name')}</Label>
                        </SimpleTooltip>
                        <Input
                            id="name"
                            value={config.name}
                            onChange={(e) => updateConfig('name', e.target.value)}
                            placeholder={isActiveDirectory ? 'e.g., Corporate AD' : 'e.g., Corporate LDAP'}
                        />
                        <SimpleTooltip content={isActiveDirectory ? 'Domain Controller address' : 'LDAP server address'} title={t('integrations.ldap.serverUrl')} position="top" maxWidth="300px">
                            <Label htmlFor="serverUrl" required className="text-left">{t('integrations.ldap.serverUrl')}</Label>
                        </SimpleTooltip>
                        <Input
                            id="serverUrl"
                            value={config.serverUrl}
                            onChange={(e) => updateConfig('serverUrl', e.target.value)}
                            placeholder={isActiveDirectory ? 'ldaps://dc.company.com' : 'ldap://ldap.company.com'}
                        />

                        {/* Row 2: Port | Input | SSL/TLS | Toggle */}
                        <SimpleTooltip content="Standard: 389 (LDAP), 636 (LDAPS)" title={t('integrations.ldap.port')} position="top" maxWidth="300px">
                            <Label htmlFor="port" className="text-left">{t('integrations.ldap.port')}</Label>
                        </SimpleTooltip>
                        <Input
                            id="port"
                            type="number"
                            value={config.port}
                            onChange={(e) => updateConfig('port', parseInt(e.target.value) || 389)}
                        />
                        <SimpleTooltip content="Enable SSL (LDAPS) or StartTLS encryption" title="Encryption" position="top" maxWidth="300px">
                            <Label className="text-left">Encryption</Label>
                        </SimpleTooltip>
                        <div className="flex items-center gap-4">
                            <Toggle
                                value={config.useSsl}
                                onChange={(val) => {
                                    updateConfig('useSsl', val);
                                    if (val) updateConfig('port', 636);
                                }}
                                trueLabel="SSL"
                                falseLabel="SSL"
                                hideText
                            />
                            <span className="text-sm text-muted-foreground">SSL</span>
                            <Toggle
                                value={config.useTls}
                                onChange={(val) => updateConfig('useTls', val)}
                                trueLabel="TLS"
                                falseLabel="TLS"
                                hideText
                            />
                            <span className="text-sm text-muted-foreground">StartTLS</span>
                        </div>

                        {/* Row 3: Enabled | Toggle | Test Connection | Button */}
                        <SimpleTooltip content="Enable or disable this integration" title={t('common.enabled')} position="top" maxWidth="300px">
                            <Label className="text-left">{t('common.enabled')}</Label>
                        </SimpleTooltip>
                        <Toggle
                            value={config.enabled}
                            onChange={(val) => updateConfig('enabled', val)}
                        />
                        <SimpleTooltip content={`Test your ${providerName} connection settings`} title={t('integrations.ldap.testConnection')} position="top" maxWidth="300px">
                            <Label className="text-left">{t('integrations.ldap.testConnection')}</Label>
                        </SimpleTooltip>
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleTestConnection}
                                disabled={testing || !config.serverUrl || !config.bindDn}
                                variant="outline"
                                size="sm"
                            >
                                {testing ? (
                                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Testing...</>
                                ) : (
                                    <><TestTube2 className="h-4 w-4 mr-2" />Test</>
                                )}
                            </Button>
                            <span className={`h-2 w-2 rounded-full ${connectionStatus === 'connected' ? 'bg-green-500' :
                                connectionStatus === 'error' ? 'bg-red-500' : 'bg-gray-400'
                                }`} />
                            <span className="text-sm capitalize text-muted-foreground">{connectionStatus}</span>
                        </div>
                    </div>
                </FormBox>

                {/* Bind Credentials */}
                <FormBox
                    title={t('integrations.ldap.bindCredentials')}
                    icon={<Lock className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
                        {/* Row 1: Bind DN | Input | Bind Password | Input */}
                        <SimpleTooltip content="Full DN of the bind user" title={t('integrations.ldap.bindDn')} position="top" maxWidth="300px">
                            <Label htmlFor="bindDn" required className="text-left">{t('integrations.ldap.bindDn')}</Label>
                        </SimpleTooltip>
                        <Input
                            id="bindDn"
                            value={config.bindDn}
                            onChange={(e) => updateConfig('bindDn', e.target.value)}
                            placeholder={isActiveDirectory ? 'CN=ServiceAccount,OU=Users,DC=company,DC=com' : 'cn=admin,dc=company,dc=com'}
                        />
                        <SimpleTooltip content="Password for the bind user" title={t('integrations.ldap.bindPassword')} position="top" maxWidth="300px">
                            <Label htmlFor="bindPassword" required className="text-left">{t('integrations.ldap.bindPassword')}</Label>
                        </SimpleTooltip>
                        <Input
                            id="bindPassword"
                            type="password"
                            value={config.bindPassword}
                            onChange={(e) => updateConfig('bindPassword', e.target.value)}
                            placeholder={editConnectionId ? '••••••••' : 'Enter password'}
                        />
                    </div>
                </FormBox>

                {/* Search Settings */}
                <FormBox
                    title={t('integrations.ldap.searchSettings')}
                    icon={<RefreshCw className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
                        {/* Row 1: Base DN | Input | User Filter | Input */}
                        <SimpleTooltip content="Base DN for user searches" title={t('integrations.ldap.baseDn')} position="top" maxWidth="300px">
                            <Label htmlFor="baseDn" required className="text-left">{t('integrations.ldap.baseDn')}</Label>
                        </SimpleTooltip>
                        <Input
                            id="baseDn"
                            value={config.baseDn}
                            onChange={(e) => updateConfig('baseDn', e.target.value)}
                            placeholder="DC=company,DC=com"
                        />
                        <SimpleTooltip content="LDAP filter for finding users" title={t('integrations.ldap.userFilter')} position="top" maxWidth="300px">
                            <Label htmlFor="userFilter" className="text-left">{t('integrations.ldap.userFilter')}</Label>
                        </SimpleTooltip>
                        <Input
                            id="userFilter"
                            value={config.userFilter}
                            onChange={(e) => updateConfig('userFilter', e.target.value)}
                            placeholder="(objectClass=person)"
                        />

                        {/* Row 2: Group Filter | Input | (empty) */}
                        <SimpleTooltip content="LDAP filter for finding groups" title={t('integrations.ldap.groupFilter')} position="top" maxWidth="300px">
                            <Label htmlFor="groupFilter" className="text-left">{t('integrations.ldap.groupFilter')}</Label>
                        </SimpleTooltip>
                        <Input
                            id="groupFilter"
                            value={config.groupFilter}
                            onChange={(e) => updateConfig('groupFilter', e.target.value)}
                            placeholder="(objectClass=group)"
                        />
                        <div /><div />
                    </div>
                </FormBox>

                {/* Attribute Mapping */}
                <FormBox
                    title={t('integrations.ldap.attributeMapping')}
                    icon={<RefreshCw className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
                        {/* Row 1: Username Attr | Input | Email Attr | Input */}
                        <SimpleTooltip content="Attribute for username" title="Username Attribute" position="top" maxWidth="300px">
                            <Label htmlFor="usernameAttribute" className="text-left">Username Attribute</Label>
                        </SimpleTooltip>
                        <Input
                            id="usernameAttribute"
                            value={config.usernameAttribute}
                            onChange={(e) => updateConfig('usernameAttribute', e.target.value)}
                            placeholder="sAMAccountName"
                        />
                        <SimpleTooltip content="Attribute for email" title="Email Attribute" position="top" maxWidth="300px">
                            <Label htmlFor="emailAttribute" className="text-left">Email Attribute</Label>
                        </SimpleTooltip>
                        <Input
                            id="emailAttribute"
                            value={config.emailAttribute}
                            onChange={(e) => updateConfig('emailAttribute', e.target.value)}
                            placeholder="mail"
                        />

                        {/* Row 2: Display Name Attr | Input | Phone Attr | Input */}
                        <SimpleTooltip content="Attribute for display name" title="Display Name Attribute" position="top" maxWidth="300px">
                            <Label htmlFor="displayNameAttribute" className="text-left">Display Name Attribute</Label>
                        </SimpleTooltip>
                        <Input
                            id="displayNameAttribute"
                            value={config.displayNameAttribute}
                            onChange={(e) => updateConfig('displayNameAttribute', e.target.value)}
                            placeholder="displayName"
                        />
                        <SimpleTooltip content="Attribute for phone number" title="Phone Attribute" position="top" maxWidth="300px">
                            <Label htmlFor="phoneAttribute" className="text-left">Phone Attribute</Label>
                        </SimpleTooltip>
                        <Input
                            id="phoneAttribute"
                            value={config.phoneAttribute}
                            onChange={(e) => updateConfig('phoneAttribute', e.target.value)}
                            placeholder="telephoneNumber"
                        />
                    </div>
                </FormBox>

                {/* Sync Settings */}
                <FormBox
                    title={t('integrations.ldap.syncSettings')}
                    icon={<RefreshCw className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
                        {/* Row 1: Enable Sync | Toggle | Sync Interval | Input */}
                        <SimpleTooltip content="Automatically sync users from directory" title={t('integrations.ldap.enableSync')} position="top" maxWidth="300px">
                            <Label className="text-left">{t('integrations.ldap.enableSync')}</Label>
                        </SimpleTooltip>
                        <Toggle
                            value={config.syncEnabled}
                            onChange={(val) => updateConfig('syncEnabled', val)}
                        />
                        {config.syncEnabled && (
                            <>
                                <SimpleTooltip content="Interval between syncs (in minutes)" title="Sync Interval (minutes)" position="top" maxWidth="300px">
                                    <Label htmlFor="syncInterval" className="text-left">Sync Interval (minutes)</Label>
                                </SimpleTooltip>
                                <Input
                                    id="syncInterval"
                                    type="number"
                                    value={config.syncInterval}
                                    onChange={(e) => updateConfig('syncInterval', parseInt(e.target.value) || 60)}
                                    min={5}
                                    max={1440}
                                />
                            </>
                        )}
                    </div>
                </FormBox>
            </div>

            {/* Fixed Action Bar with Save / Save and Close */}
            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                isEditMode={isEditMode}
                isSaving={isSaving}
                cancelText={t('common.cancel')}
                useSaveDropdown={true}
                saveDropdownStorageKey="ldap-integration:saveAction"
                saveDisabled={isSaving}
                availableSaveActions={['save', 'save-and-close']}
            />
        </div>
    );
};
